import { useEffect, useRef, useState, type ReactNode } from "react";
import { Carousel } from "@mantine/carousel";
import type { EmblaCarouselType } from "embla-carousel";
import {
  ECOSYSTEM_EXPLORE_CARD_HEIGHT,
  ECOSYSTEM_EXPLORE_CARD_WIDTH,
} from "@/pages/ecosystem-heads/components/ecosystemCorridorPromo.util";

const AUTOPLAY_DELAY_MS = 4500;
const CARD_SLIDE_SIZE = `${ECOSYSTEM_EXPLORE_CARD_WIDTH}px`;

type EcosystemExploreCardCarouselProps = {
  children: ReactNode;
  itemCount: number;
};

/** Auto-rotating horizontal carousel with fixed-width (300px) explore corridor cards. */
export default function EcosystemExploreCardCarousel({
  children,
  itemCount,
}: EcosystemExploreCardCarouselProps) {
  const [embla, setEmbla] = useState<EmblaCarouselType | null>(null);
  const pausedRef = useRef(false);
  const multi = itemCount > 1;

  useEffect(() => {
    if (!embla || !multi) return;

    const advance = () => {
      if (pausedRef.current) return;
      if (embla.canScrollNext()) {
        embla.scrollNext();
      } else {
        embla.scrollTo(0);
      }
    };

    const id = window.setInterval(advance, AUTOPLAY_DELAY_MS);
    return () => window.clearInterval(id);
  }, [embla, multi]);

  return (
    <Carousel
      getEmblaApi={setEmbla}
      slideSize={CARD_SLIDE_SIZE}
      slideGap="md"
      align="start"
      slidesToScroll={1}
      withControls={multi}
      controlsOffset={0}
      loop={multi}
      onMouseEnter={() => {
        pausedRef.current = true;
      }}
      onMouseLeave={() => {
        pausedRef.current = false;
      }}
      styles={{
        slide: { height: ECOSYSTEM_EXPLORE_CARD_HEIGHT },
        control: {
          backgroundColor: "#FFFFFF",
          border: "1px solid #E2E8F0",
          boxShadow: "0 2px 8px rgba(15, 23, 42, 0.1)",
          "&[data-inactive]": { opacity: 0.4, cursor: "default" },
        },
      }}
    >
      {children}
    </Carousel>
  );
}

export function EcosystemExploreCarouselSlide({ children }: { children: ReactNode }) {
  return <Carousel.Slide>{children}</Carousel.Slide>;
}
